import { Injectable, inject } from '@angular/core';
import { Store } from '@ngxs/store';
import { QualificationTrainings } from '@wilson/interfaces';
import {
  CheckQualificationDto,
  CheckUserQualificationResult,
  UserQualificationsGateway,
} from '@wilson/qualification-v2/common';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  from,
} from 'rxjs';
import { map, shareReplay, switchMap, tap } from 'rxjs/operators';

export interface UserQualificationCheckResultWithTrainingId
  extends CheckUserQualificationResult {
  qualificationTrainingId: string;
}

export interface NewQualificationFromTrainings {
  qualificationId: string;
  userId: string;
  qualificationTrainingId: string;
}

export interface ExistingQualificationFromTrainings {
  qualificationId: string;
  userQualificationId: string;
  userId: string;
  qualificationTrainingId: string;
}

@Injectable({
  providedIn: 'root',
})
export class TrainingQualificationsFinalizeDrawerService {
  private readonly userQualificationsGateway = inject(
    UserQualificationsGateway,
  );
  private readonly store = inject(Store);

  private _finalizeDrawerVisible = new ReplaySubject<boolean>(1);
  private _trainingsToFinalize = new ReplaySubject<QualificationTrainings[]>(1);
  private _loading = new BehaviorSubject<boolean>(false);
  private _successfullyFinalizedTrainings = new Subject<void>();

  public finalizeDrawerVisible$ = this._finalizeDrawerVisible.asObservable();
  public trainingsToFinalize$ = this._trainingsToFinalize.asObservable();
  public loading$ = this._loading.asObservable();
  public successfullyFinalizedTrainings$ =
    this._successfullyFinalizedTrainings.asObservable();

  private qualificationCheckResults$: Observable<
    UserQualificationCheckResultWithTrainingId[]
  > = this.trainingsToFinalize$.pipe(
    tap(() => this._loading.next(true)),
    switchMap((trainings) => {
      if (!trainings?.length) return from([[]]);

      // Create a Set to store unique combinations of userId and qualificationId
      // (there is no constraint for identical userId / qualificationId combinations from trainings)
      const uniqueChecks = new Set<string>();
      const qualificationsToCheck: CheckQualificationDto[] = [];
      const trainingIdMap = new Map<string, string>();

      trainings.forEach((training) => {
        training.qualifications.forEach((qualification) => {
          const key = `${training.userId}-${qualification.id}`;
          if (!uniqueChecks.has(key)) {
            uniqueChecks.add(key);
            qualificationsToCheck.push({
              userId: training.userId,
              qualificationId: qualification.id,
            });
            trainingIdMap.set(key, training.id);
          }
        });
      });

      return this.userQualificationsGateway
        .checkUserQualificationsExists(qualificationsToCheck)
        .pipe(
          // Reattach qualificationTrainingId to the results (required for bulk create / update)
          map((results) =>
            results.map((result) => {
              const key = `${result.userId}-${result.qualificationId}`;
              const trainingId = trainingIdMap.get(key);

              if (!trainingId) {
                throw new Error(
                  `No training ID found. This should never happen.`,
                );
              }

              return {
                ...result,
                qualificationTrainingId: trainingId,
              };
            }),
          ),
        );
    }),
    shareReplay(1),
  );

  public newQualificationFromTrainings$: Observable<
    NewQualificationFromTrainings[]
  > = this.qualificationCheckResults$.pipe(
    map((results) => {
      return results
        .filter((result) => !result.id)
        .map((result) => ({
          qualificationId: result.qualificationId,
          userId: result.userId,
          qualificationTrainingId: result.qualificationTrainingId,
        }));
    }),
    tap(() => this._loading.next(false)),
    shareReplay(1),
  );

  public existingQualificationsFromTrainings$: Observable<
    ExistingQualificationFromTrainings[]
  > = this.qualificationCheckResults$.pipe(
    map((results) => {
      return results
        .filter((result) => result.id)
        .map((result) => ({
          qualificationId: result.qualificationId,
          userQualificationId: result.id as string,
          userId: result.userId,
          qualificationTrainingId: result.qualificationTrainingId,
        }));
    }),
    tap(() => this._loading.next(false)),
    shareReplay(1),
  );

  public totalQualificationsToCreateOrExtend$ = combineLatest([
    this.newQualificationFromTrainings$,
    this.existingQualificationsFromTrainings$,
  ]).pipe(
    map(([newQuals, existingQuals]) => newQuals.length + existingQuals.length),
  );

  public setFinalizeDrawerVisible(visible: boolean): void {
    this._finalizeDrawerVisible.next(visible);
  }

  public setTrainingsToFinalize(trainings: QualificationTrainings[]): void {
    this._trainingsToFinalize.next(trainings);
  }

  public triggerSuccessfullyFinalizedTrainings(): void {
    this._successfullyFinalizedTrainings.next();
  }
}
